import React, { useMemo } from 'react';
import { MessageSquare, Bot } from 'lucide-react';
import { ChatMessage } from '@/types/chat';
import MessageHeader from './components/MessageHeader';
import MessageContent from './components/MessageContent';
import VehicleDataChip from './components/VehicleDataChip';
import PartsDisplay from './components/PartsDisplay';
import FEDisplay from './components/FEDisplay';
import MarkdownRenderer from '@/components/MarkdownRenderer';
import { usePartsGrouping } from './hooks/usePartsGrouping';
import './ChatMessageAssistant.css';

interface ChatMessageAssistantProps {
  message: ChatMessage;
  showTimestamp?: boolean;
  compact?: boolean;
  isLoading?: boolean;
  error?: string;
  pageRoute?: string;
}

const ChatMessageAssistant: React.FC<ChatMessageAssistantProps> = ({
  message,
  showTimestamp = true,
  compact = false,
  isLoading = false,
  error,
  pageRoute = 'home'
}) => {
  const { pcdbParts, vmrsParts, totalCount, avgScore } = usePartsGrouping({
    pcdbParts: message.pcdbParts || [],
    vmrsParts: message.vmrsParts || []
  });

  const hasPartsData = (message.pcdbParts && message.pcdbParts.length > 0) || 
                      (message.vmrsParts && message.vmrsParts.length > 0) ||
                      message.pt;
  
  const hasFEData = message.mode === 'fe' && message.feResponse;
  const isMarkdownResponse = message.responseType === 'markdown';

  // Add error handling for markdown content
  const renderContent = () => {
    if (isMarkdownResponse && message.markdownContent) {
      try {
        return <MarkdownRenderer content={message.markdownContent} />;
      } catch (err) {
        console.error('Error rendering markdown:', err);
        return <MessageContent content={message.content || 'Error rendering markdown content'} />;
      }
    }
    return <MessageContent content={message.content} />;
  };

  return (
    <div className={`chat-message-assistant-modern ${compact ? 'compact' : ''}`}>
      <div className="message-container-modern">
        <div className="avatar-section-modern">
          <div className="message-avatar-modern assistant">
            <Bot size={20} />
          </div>
          <div className="avatar-status-modern" />
        </div>

        <div className="message-content-modern">
          <MessageHeader 
            showTimestamp={showTimestamp}
            timestamp={message.timestamp}
            pageRoute={pageRoute}
          />

          {message.vehicleData && (
            <VehicleDataChip vehicleData={message.vehicleData} />
          )}

          {renderContent()}

          {hasFEData && !isMarkdownResponse && (
            <div className="parts-section-modern">
              <FEDisplay
                feResponse={message.feResponse!}
                isLoading={isLoading}
                error={error}
              />
            </div>
          )}

          {(hasPartsData || isLoading || error) && !isMarkdownResponse && !hasFEData && (
            <div className="parts-section-modern">
              <PartsDisplay
                pcdbParts={pcdbParts}
                vmrsParts={vmrsParts}
                pt={message.pt}
                isLoading={isLoading}
                error={error}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatMessageAssistant;
